import React, {
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { getPasscodeLists } from '../../../store/actions/admin/passcode-lists';
import {
	getPeopleSearchCount,
	updatePeopleSearch,
} from '../../../store/actions/admin/people';
import { EventsState } from '../../../store/types';
import { PasscodeList, PASSCODE_LIST_TYPE } from '../../../types/working-model';
import Icon, { COLORS, ICONS } from '../../general-ui/icon';
import Paging from '../../general-ui/paging/paging';
import SelectInput from '../../general-ui/select/select';
import TabHeader from '../../general-ui/tab-header/tab-header';
import AddPasscodeModal from './add-passcode-list';
import PasscodeListEditor from './passcode-list';
import PasscodeTile from './passcode-tile';
import Search from '../../general-ui/search-bar/search';
import './people-main.scss';
import { removeAllMembers } from '../../../store/actions/admin/passcode-list-members';
import PeopleList from './people-list';
import PaidListEditor from './paid-list';
import useTranslationModules from '../../../i18n/useTranslationModules';


export interface WorkingPasscodeList {
	type: PASSCODE_LIST_TYPE;
	name: string;
	max_uses: number;
	master_passcode: string;
	url: string;
}

const sortByOptions = [
	{
		label: 'Newest',
		value: 'newest',
	},
	{
		label: 'Oldest',
		value: 'oldest',
	},
	{
		label: 'A-Z',
		value: 'a-z',
	},
	{
		label: 'Z-A',
		value: 'z-a',
	},
	{
		label: 'Most members',
		value: 'highcount',
	},
	{
		label: 'Least members',
		value: 'lowcount',
	},
];

enum Tabs {
	registered = "Registered Users",
	passcodes = "Passcode Lists",
	paidlists = "Paid Lists"
}

export default function PeopleMain(): JSX.Element {
	const {
		AuthReducer: { user, token },
		PasscodeListReducer: { passcodeLists },
		PaidListMemberReducer: { selectedPaidList }
	} = useSelector((state: EventsState) => state);

	const dispatch = useDispatch();
	useTranslationModules({ language: "en" });

	const [currentListPage, setCurrentListPage] = useState(1);
	const [activeTab, setActiveTab] = useState<Tabs>(Tabs.registered);
	const [passcodeModalOpen, setPasscodeModalOpen] = useState(false);
	const [currentPasscode, setCurrentPasscode] = useState<PasscodeList | null>(
		null
	);
	const [passcodeListOpen, setPasscodeListOpen] = useState(false);

	const [sortBy, setSortBy] = useState('');
	const [currentPasscodeLists, setCurrentPasscodeLists] = useState<
		PasscodeList[] | null
	>(null);

	const [currentPasscodeListSearch, setCurrentPasscodeListSearch] = useState('');
	const [currentPaidListSearch, setCurrentPaidListSearch] = useState('');
	const [currentRegisteredUsersSearch, setCurrentRegisteredUsersSearch] = useState('');

	useEffect(() => {
		batch(() => {
			dispatch(getPeopleSearchCount(token!, user!.active_channel, ''));
			dispatch(getPasscodeLists(user!.active_channel, token!));
		});
	}, [dispatch, token, user]);

	useEffect(() => {
		setCurrentPasscodeLists(passcodeLists);
	}, [passcodeLists]);

	//If the user clicks away from this list and comes back, we want the search to be cleared.
	useEffect(() => {
		dispatch(updatePeopleSearch(''));
		setCurrentRegisteredUsersSearch('');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTab]);

	const handleUserSearch = (search: string) => {
		setCurrentRegisteredUsersSearch(search);
		batch(() => {
			dispatch(updatePeopleSearch(search));
			dispatch(getPeopleSearchCount(token!, user!.active_channel, search));
		});
	};

	const checkEmptySearch = (search: string) => {
		if (search === '') {
			handleUserClear();
		}
	};

	const handleUserClear = () => {
		batch(() => {
			dispatch(getPeopleSearchCount(token!, user!.active_channel, ''));
			dispatch(updatePeopleSearch(''));
		});
	};

	function setPasscodeEdit(list: PasscodeList) {
		setCurrentPasscode(list);
		setPasscodeListOpen(true);
	}

	const closePasscodeList = useCallback(() => {
		setActiveTab(Tabs.passcodes);
		setPasscodeListOpen(false);
		setCurrentPasscode(null);
		dispatch(
			removeAllMembers()
		);
	}, [dispatch]);

	const handleSortByChange = useCallback(
		(value: string) => {
			setCurrentPasscodeListSearch('');
			setSortBy(value);
			setCurrentListPage(1);
			if (currentPasscodeLists) {
				switch (value) {
					case 'newest': {
						const array = passcodeLists.sort((a, b) => {
							const dateA = new Date(a.created_at);
							const dateB = new Date(b.created_at);

							return dateA > dateB ? -1 : 1;
						});
						setCurrentPasscodeLists(array);
						break;
					}
					case 'oldest': {
						const array = passcodeLists.sort((a, b) => {
							const dateA = new Date(a.created_at);
							const dateB = new Date(b.created_at);

							return dateA < dateB ? -1 : 1;
						});
						setCurrentPasscodeLists(array);
						break;
					}
					case 'a-z': {
						const array = passcodeLists.sort((a, b) => {
							const nameA = a.name;
							const nameB = b.name;

							return nameA < nameB ? -1 : 1;
						});
						setCurrentPasscodeLists(array);
						break;
					}
					case 'z-a': {
						const array = passcodeLists.sort((a, b) => {
							const nameA = a.name;
							const nameB = b.name;

							return nameA > nameB ? -1 : 1;
						});
						setCurrentPasscodeLists(array);
						break;
					}
					case 'highcount': {
						const array = passcodeLists.sort((a, b) => {
							const countA = a.member_count;
							const countB = b.member_count;

							return countA > countB ? -1 : 1;
						});
						setCurrentPasscodeLists(array);
						break;
					}
					case 'lowcount': {
						const array = passcodeLists.sort((a, b) => {
							const countA = a.member_count;
							const countB = b.member_count;

							return countA < countB ? -1 : 1;
						});
						setCurrentPasscodeLists(array);
						break;
					}
					default:
						return;
				}
			}
		},
		[currentPasscodeLists, passcodeLists]
	);

	const handleSetPasscodeModal = useCallback(() => {
		setPasscodeModalOpen(!passcodeModalOpen);
	}, [passcodeModalOpen]);

	const handleClosePasscodeModalSubmit = useCallback(() => {
		setCurrentPasscodeListSearch('');
		setPasscodeModalOpen(!passcodeModalOpen);
	}, [passcodeModalOpen]);

	const handleListSearch = (search: string) => {
		setCurrentPasscodeListSearch(search);
		if (currentPasscodeLists) {
			const array: PasscodeList[] = passcodeLists?.filter((list) =>
				list.name.toLowerCase().includes(search.toLowerCase())
			);
			setCurrentPasscodeLists(array);
		}
	};

	const handlePaidListSearch = useCallback((search: string) => {
		setCurrentPaidListSearch(search);
	}, []);

	const handlePaidListClear = useCallback(() => {
		setCurrentPaidListSearch('');
	}, []);

	const handleListClear = () => {
		setCurrentPasscodeLists(passcodeLists);
	};

	const passocdeTileList = useMemo(() => {
		if (currentPasscodeLists) {
			return currentPasscodeLists.map((list: PasscodeList, index: number) => {
				if (
					index >= (currentListPage - 1) * 15 &&
					index < currentListPage * 15
				) {
					return (
						<PasscodeTile
							key={list.uuid}
							list={list}
							onClick={() => setPasscodeEdit(list)}
						/>
					);
				} else {
					return;
				}
			});
		} else {
			return;
		}
		//sortBy is needed here to trigger a rerender upon the sort changing
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentListPage, currentPasscodeLists, sortBy]);

	const renderTab = useMemo(() => {
		switch (activeTab) {
			case Tabs.registered: {
				return (
					<PeopleList />
				);
			}
			case Tabs.passcodes: {
				return (
					<>
						{passcodeListOpen ? (
							<PasscodeListEditor
								currentPasscode={currentPasscode}
								onClick={closePasscodeList}
							/>
						) : (
							<>
								<div className="passcodelist-container">{passocdeTileList}</div>
								{currentPasscodeLists ? (
									<div className="passcodelist-pagination">
										<Paging
											page={currentListPage}
											setPage={setCurrentListPage}
											totalPages={
												Math.floor(currentPasscodeLists?.length / 15) + 1
											}
										/>
									</div>
								) : (
									<></>
								)}
							</>
						)}
					</>
				);
			}
			case Tabs.paidlists: {
				return (
					<PaidListEditor search={currentPaidListSearch} />
				);
			}
		}
	}, [activeTab, closePasscodeList, currentListPage, currentPaidListSearch, currentPasscode, currentPasscodeLists, passcodeListOpen, passocdeTileList]);


	return (
		<div className="people-wrapper">
			<div className="people-container">
				{currentPasscode || selectedPaidList ? (
					<></>
				) : (
					<>
						<h2>People</h2>
						<div className="people-header">
							<TabHeader
								tabs={[Tabs.registered, Tabs.passcodes, Tabs.paidlists]}
								onClick={setActiveTab}
								currentTab={activeTab}
							/>
							<div className="people-header-right">
								{activeTab === Tabs.registered || currentPasscode ? (
									<div>
										<Search
											onSearch={handleUserSearch}
											onClear={handleUserClear}
											onChange={checkEmptySearch}
											isControlled={true}
											controlledValue={currentRegisteredUsersSearch}
										/>
									</div>
								) : (
									<div></div>
								)}
								{activeTab === Tabs.registered ? (
									<div className="header-buttons">
										{/*<button>Download</button>*/}
									</div>
								) : (
									<>
										{activeTab === Tabs.passcodes ? (
											<>
												<Search
													onSearch={handleListSearch}
													onClear={handleListClear}
													isControlled={true}
													controlledValue={currentPasscodeListSearch}
												/>
												<div className="sort-container">
													<SelectInput
														placeholder="Sort by"
														mainIcon={
															<Icon
																name={ICONS.KEYBOARD_ARROW_DOWN}
																size={14}
																color="white"
															/>
														}
														options={sortByOptions}
														selected={sortBy}
														small
														noMaxHeight
														onChange={handleSortByChange}
														disabled={passcodeListOpen}
													/>
												</div>
												<div className="header-buttons">
													<button onClick={handleSetPasscodeModal} className="small primary">
														<Icon name={ICONS.ADD} size={12} color={COLORS.BLACK} />
														Add passcode list
													</button>
												</div>
											</>
										) : (
											<>
												<Search
													onSearch={handlePaidListSearch}
													onClear={handlePaidListClear}
													isControlled={true}
													controlledValue={currentPaidListSearch}
												/>
											</>
										)}
									</>

								)}
							</div>
						</div>
					</>
				)}

				{renderTab}
				<AddPasscodeModal
					modalOpen={passcodeModalOpen}
					setModalClosedCancel={handleSetPasscodeModal}
					setModalClosedSubmit={handleClosePasscodeModalSubmit}
				/>
			</div>
		</div>
	);
}
